import React, { useEffect, useState, useRef } from 'react';
import ReportNote from '../components/ReportNote';

const API = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

import { localDateStr, monthRange, fmtDate, useDrawerFocus } from './utils';

function monthsInRange(from, to) {
  const d1 = new Date(from), d2 = new Date(to);
  return Math.max(1, Math.round((d2 - d1) / (30 * 24 * 3600 * 1000)));
}

function fmtH(secs) {
  if (!secs) return '—';
  const h = Math.floor(secs / 3600);
  const m = Math.round((secs % 3600) / 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

const PRIORITY_COLORS = { Critical: '#dc2626', Highest: '#dc2626', High: '#d97706', Medium: '#f59e0b', Low: '#16a34a', Lowest: '#16a34a' };

function TicketsDrawer({ project, name, fromDate, toDate, onClose }) {
  const [tickets, setTickets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [jiraUrl, setJiraUrl] = useState(localStorage.getItem('jiraUrl') || '');
  const drawerRef = useRef(null);
  useDrawerFocus(drawerRef);

  useEffect(() => {
    fetch(`${API}/api/settings`).then(r => r.json()).then(d => {
      if (d.jira_url) { setJiraUrl(d.jira_url); localStorage.setItem('jiraUrl', d.jira_url); }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/api/reports/customers/tickets?project=${encodeURIComponent(project)}&from_date=${fromDate}&to_date=${toDate}`)
      .then(r => r.json())
      .then(d => { setTickets(d.tickets || []); setLoading(false); })
      .catch(() => { setTickets([]); setLoading(false); });
  }, [project, fromDate, toDate]);

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  return (
    <>
      <div onClick={onClose} className="drawer-overlay" />
      <div className="drawer drawer--lg" role="dialog" aria-modal="true"
        aria-label={`Tickets — ${name}`} tabIndex={-1} ref={drawerRef}>
        <div className="drawer-header drawer-header--gap">
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 17 }}>{name}</div>
            <div className="drawer-subtitle">{tickets ? tickets.length : '…'} tickets · {fromDate} → {toDate}</div>
          </div>
          <button onClick={onClose} className="drawer-close-btn">
            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div className="drawer-body" style={{ padding: '16px 24px' }}>
          {loading ? <div className="loading">Loading tickets…</div> : (!tickets || tickets.length === 0) ? (
            <div className="empty"><p>No tickets in this period.</p></div>
          ) : (
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              {tickets.map((t, idx) => (
                <div key={t.key} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 14px', borderBottom: idx < tickets.length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3, flexWrap: 'wrap' }}>
                      {jiraUrl
                        ? <a href={`${jiraUrl}/browse/${t.key}`} target="_blank" rel="noopener noreferrer" style={{ fontFamily: 'DM Mono, monospace', fontSize: 12, fontWeight: 600, color: 'var(--accent)', textDecoration: 'none', background: 'var(--accent-light)', padding: '1px 7px', borderRadius: 5 }}>{t.key} ↗</a>
                        : <span className="badge badge-blue" style={{ fontFamily: 'DM Mono, monospace', fontSize: 11 }}>{t.key}</span>}
                      <span className="badge badge-default" style={{ fontSize: 11 }}>{t.status}</span>
                      <span style={{ fontSize: 11, padding: '1px 6px', borderRadius: 100, background: (PRIORITY_COLORS[t.priority] || '#94a3b8') + '22', color: PRIORITY_COLORS[t.priority] || '#94a3b8', fontWeight: 600 }}>{t.priority || '—'}</span>
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.4, wordBreak: 'break-word' }}>{t.summary}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 3 }}>{t.assignee || 'Unassigned'}</div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0, fontSize: 11, color: 'var(--text-2)' }}>
                    <div>{t.status}</div>
                    <div>{fmtDate(t.resolved_at || (t.created || '').slice(0, 10))}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default function CustomerReport() {
  const [offset, setOffset] = useState(0);
  const [range, setRange] = useState(monthRange(0));
  const [custom, setCustom] = useState(false);
  const [fromDate, setFromDate] = useState(monthRange(0).from);
  const [toDate, setToDate] = useState(monthRange(0).to);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('total');
  const [omniUsers, setOmniUsers] = useState(null); // {projectKey: {count, name}}
  const [omniLoading, setOmniLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null); // {project, name}

  useEffect(() => {
    setOmniLoading(true);
    fetch(`${API}/api/omni-customers?omni365_only=true`)
      .then(r => r.json())
      .then(d => {
        const map = {};
        (d.customers || []).forEach(c => {
          if (c.is_omni) map[c.project_key] = { count: c.user_count, name: c.customer_name || c.name };
        });
        setOmniUsers(map);
        setOmniLoading(false);
      })
      .catch(() => { setOmniUsers({}); setOmniLoading(false); });
  }, []);

  const load = (f, t) => {
    setLoading(true);
    fetch(`${API}/api/reports/customers?from_date=${f}&to_date=${t}`)
      .then(r => r.json()).then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    if (!custom) {
      const r = monthRange(offset);
      setRange(r); setFromDate(r.from); setToDate(r.to);
      load(r.from, r.to);
    }
  }, [offset, custom]);

  const exportCSV = () => {
    window.location.href = `${API}/api/reports/export/csv?type=customers&from_date=${fromDate}&to_date=${toDate}`;
  };

  const customers = data
    ? [...data.customers]
        .filter(c => omniUsers && omniUsers.hasOwnProperty(c.project))
        .filter(c => {
          const fullName = omniUsers[c.project]?.name || c.name;
          const q = search.toLowerCase();
          return fullName.toLowerCase().includes(q) || c.project.toLowerCase().includes(q);
        })
        .sort((a, b) => b[sortBy] - a[sortBy])
    : [];

  const maxTickets = customers.length ? Math.max(...customers.map(c => c.total), 1) : 1;

  return (
    <div>
      <ReportNote what="Billable tickets and logged time per customer project." from={fromDate} to={toDate}
                  note="Tickets with the Billable box unticked are excluded. The box only came into use in August 2026, so work from before then is counted as billable whether or not it was ticked." />
      {/* Controls */}
      <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        {!custom ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={() => setOffset(o => o - 1)} className="btn btn-secondary" style={{ padding: '6px 12px' }}>‹</button>
            <span style={{ fontWeight: 700, fontSize: 15, minWidth: 180, textAlign: 'center' }}>{range.label}</span>
            <button onClick={() => setOffset(o => o + 1)} className="btn btn-secondary" style={{ padding: '6px 12px' }}>›</button>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="input" style={{ width: 140 }} />
            <span style={{ color: 'var(--text-2)' }}>→</span>
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="input" style={{ width: 140 }} />
            <button onClick={() => load(fromDate, toDate)} className="btn btn-primary" style={{ padding: '6px 14px' }}>Apply</button>
          </div>
        )}
        <button onClick={() => setCustom(c => !c)} className="btn btn-secondary" style={{ padding: '6px 12px' }}>
          {custom ? 'Monthly view' : 'Custom range'}
        </button>
        <input
          className="input no-print"
          placeholder="Search customer…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ width: 180 }}
        />
        <select className="upload-select no-print" value={sortBy} onChange={e => setSortBy(e.target.value)}>
          <option value="total">Sort by total</option>
          <option value="closed">Sort by closed</option>
          <option value="open">Sort by open</option>
        </select>
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          <button onClick={exportCSV} className="btn btn-secondary">↓ CSV</button>
          <button onClick={() => window.print()} className="btn btn-secondary">🖨 Print / PDF</button>
        </div>
      </div>

      {loading && <div className="loading">Loading customer data…</div>}

      {!loading && data && (
        <>
          <div className="grid-3" style={{ marginBottom: 24 }}>
            <div className="card">
              <div className="card-title">Total Customers</div>
              <div className="card-value">{data.customers.length}</div>
            </div>
            <div className="card">
              <div className="card-title">Total Tickets</div>
              <div className="card-value" style={{ color: 'var(--accent)' }}>{data.total_tickets}</div>
            </div>
            <div className="card">
              <div className="card-title">Showing</div>
              <div className="card-value">{customers.length}</div>
              <div className="card-sub">{fromDate} → {toDate}</div>
            </div>
          </div>

          <div className="card">
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 18, fontFamily: 'Syne, sans-serif' }}>
              Customer Activity — {fromDate} to {toDate}
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Customer</th>
                    <th>Project</th>
                    <th style={{ textAlign: 'right' }}>Total</th>
                    <th style={{ minWidth: 120 }}>Breakdown</th>
                    <th style={{ textAlign: 'right' }}>Hours</th>
                    <th style={{ textAlign: 'right' }}>Omni users</th>
                    <th style={{ textAlign: 'right' }}>
                      Included
                      <div style={{ fontSize: 9, fontWeight: 400, color: 'var(--text-3)', textTransform: 'none', letterSpacing: 0 }}>
                        Omni users × 30m
                      </div>
                    </th>
                    <th style={{ minWidth: 130 }}>
                      Support ratio
                      <div style={{ fontSize: 9, fontWeight: 400, color: 'var(--text-3)', textTransform: 'none', letterSpacing: 0 }}>
                        logged / included
                      </div>
                    </th>
                    <th>Last Activity</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map((c, i) => {
                    const months = monthsInRange(fromDate, toDate);
                    const omniEntry = omniUsers ? omniUsers[c.project] : null;
                    const omniCount = omniEntry ? omniEntry.count : 0;
                    const fullName = omniEntry?.name || c.name;
                    const includedSecs = omniCount ? omniCount * 30 * 60 * months : 0;
                    const ratio = includedSecs > 0 ? Math.round(c.hours_secs / includedSecs * 100) : null;
                    const ratioColor = ratio == null ? 'var(--text-3)' : ratio > 100 ? 'var(--critical)' : ratio > 80 ? 'var(--warn)' : 'var(--ok)';
                    return (
                    <tr key={c.project}>
                      <td style={{ color: 'var(--text-3)', fontFamily: 'DM Mono, monospace', fontSize: 11 }}>{i + 1}</td>
                      <td style={{ fontWeight: 600 }}>{fullName}</td>
                      <td><span className="badge badge-blue">{c.project}</span></td>
                      <td
                        onClick={() => setSelectedCustomer({ project: c.project, name: fullName })}
                        style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', fontWeight: 700, color: 'var(--accent)', cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: 3 }}
                        title="Show tickets">
                        {c.total}
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <div style={{ flex: 1, height: 6, background: 'var(--border)', borderRadius: 99, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${(c.total / maxTickets) * 100}%`, background: 'var(--accent)', borderRadius: 99 }} />
                          </div>
                          <span style={{ fontSize: 10, color: 'var(--ok)', whiteSpace: 'nowrap' }}>{c.closed}✓</span>
                          {c.open > 0 && <span style={{ fontSize: 10, color: 'var(--warn)' }}>{c.open}↑</span>}
                        </div>
                      </td>
                      <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', color: 'var(--text-2)', fontSize: 12 }}>
                        {c.hours_secs > 0 ? c.hours_display : '—'}
                      </td>
                      <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', fontSize: 12, color: omniCount ? 'var(--text-2)' : 'var(--text-3)' }}>
                        {omniLoading ? (
                          <span style={{ color: 'var(--text-3)', fontSize: 10 }}>…</span>
                        ) : omniCount || '—'}
                      </td>
                      <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', fontSize: 12, color: includedSecs ? 'var(--text-2)' : 'var(--text-3)' }}>
                        {omniLoading ? (
                          <span style={{ color: 'var(--text-3)', fontSize: 10 }}>…</span>
                        ) : omniCount ? (
                          <span title={`${omniCount} users × 30m × ${months} month(s)`}>{fmtH(includedSecs)}</span>
                        ) : '—'}
                      </td>
                      <td>
                        {omniLoading ? (
                          <span style={{ color: 'var(--text-3)', fontSize: 10 }}>…</span>
                        ) : ratio != null ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div style={{ flex: 1, height: 5, background: 'var(--border)', borderRadius: 99, overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: `${Math.min(ratio, 100)}%`, background: ratioColor, borderRadius: 99 }} />
                            </div>
                            <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 12, fontWeight: 700, color: ratioColor, minWidth: 38, textAlign: 'right' }}>
                              {ratio}%
                            </span>
                          </div>
                        ) : '—'}
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{c.last_activity || '—'}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {selectedCustomer && (
        <TicketsDrawer
          project={selectedCustomer.project}
          name={selectedCustomer.name}
          fromDate={fromDate}
          toDate={toDate}
          onClose={() => setSelectedCustomer(null)}
        />
      )}

      <style>{`
        @media print {
          .no-print { display: none !important; }
          .sidebar, .topbar { display: none !important; }
          .main { margin: 0 !important; padding: 20px !important; }
        }
      `}</style>
    </div>
  );
}
